import styled from '@emotion/styled';
import React from 'react';
import { Image } from '~/shared/components';
import Flex from '~/shared/components/Flex/Flex';
import { useTranslation } from '~/shared/utils/translation';
import { Image as ImageType } from '~/lib/data-contract/salesforce';
import {
    SalesforceProductSpecialPrice,
    SalesforcePromotionCountdown,
} from '~/lib/salesforce/types';
import { getIsPromotionDiscountHeldBack } from '~/shared/utils/get-is-promotion-discount-held-back';
import useProductPersonalization from '~/services/personalization/useProductPersonalization';
import { TPrice } from '../../ProductCard/components/Price';
import { useSpecialPrice } from '~/services/special-price';
import { ProductPrice } from '~/shared/ProductPrice/ProductPrice';

export const ImageWrap = styled.div<{ large?: boolean }>(({ theme, large }) => ({
    height: large ? 96 : 72,
    width: large ? 96 : 72,
    background: theme.colors.productImageBackround,
    img: {
        mixBlendMode: 'multiply',
    },
}));

const DetailsWrap = styled.div(() => ({ flex: 1 }));
const PriceWrap = styled.div(({ theme }) => ({
    flexShrink: 0, // This ensures to not wrap price in 2 lines in case of long item description
    padding: `${theme.space[2]} ${theme.space[3]}`,
    border: `1px solid ${theme.colors.grey35}`,
    borderRadius: theme.sizes.cornerRadius,
    fontSize: '12px',
}));

const Brand = styled.div(({ theme }) => ({
    ...theme.mixins.useTextStyle('formsLabelActive'),
    fontWeight: theme.fontWeights.medium,
}));
const Name = styled.div(({ theme }) => ({
    fontSize: '12px',
    lineHeight: '14px',
    fontWeight: theme.fontWeights.medium,
}));
const Description = styled.div(({ theme }) => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100px',
    ...theme.mixins.useTextStyle('formsLabelActive'),
}));

const Personalization = styled.div(() => ({
    fontSize: '10px',
    lineHeight: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: '4px',

    em: {
        fontStyle: 'italic',
    },
}));

const Content = styled(Flex)(({ theme }) => ({
    padding: `${theme.space[3]} 0 ${theme.space[3]} ${theme.space[3]}`,
}));

type ComponentProps = {
    brand?: string;
    name?: string;
    quantity?: string | number;
    description?: string;
    image?: ImageType | undefined;
    large?: boolean;
    price?: number | string;
    memberprice?: number;
    quantitySelector?: React.ReactNode;
    showMemberPrice?: boolean;
    unitPrice?: number;
    c_personalization_metadata?: string;
    c_prices?: TPrice[];
    c_specialPrices?: SalesforceProductSpecialPrice;
    id?: string;
    c_group?: string;
    c_activepromo?: {
        promotionid?: string;
        promotiontype?: string;
        type?: string;
    };
    c_minimumPrice?: number;
    c_promotionType?: { type?: string; isInPromotion?: boolean };
    c_promotionCountdown?: SalesforcePromotionCountdown[];
};
export const SmallProductCard = (props: ComponentProps) => {
    const {
        brand,
        name,
        quantity,
        description,
        quantitySelector,
        large,
        image,
        price,
        memberprice,
        showMemberPrice,
        unitPrice,
        c_prices,
        c_specialPrices,
        id,
        c_minimumPrice,
        c_activepromo,
        c_group,
    } = props;

    const { translate } = useTranslation();
    const {
        shouldAllowPersonalization,
        personalizationMetadataEntries,
        addPersonalizationCostToPrice,
    } = useProductPersonalization(props);
    const isDiscountHeldBack = getIsPromotionDiscountHeldBack(props);
    const displayPrice =
        showMemberPrice && memberprice && !isDiscountHeldBack ? memberprice : unitPrice ?? price;
    const shouldShowPersonalizationDetails =
        shouldAllowPersonalization && !!personalizationMetadataEntries;
    const isGiftCard = c_group === 'electronicgiftcard' || c_group === 'physicalgiftcard';

    const { prices: specialPriceList } = useSpecialPrice({
        id: id ?? '',
        c_prices,
        c_specialPrices,
    });

    const priceLabel = `${quantity} ${translate(
        'checkout.summery.product-quantity',
    )} x ${addPersonalizationCostToPrice(displayPrice)}`;

    return (
        <Flex alignItems="center" justifyContent="space-between">
            <ImageWrap large={large}>
                {image && (
                    <Image
                        layout="fixed"
                        width={large ? 96 : 72}
                        height={large ? 96 : 72}
                        objectFit="cover"
                        src={image.link}
                    />
                )}
            </ImageWrap>
            <Content flex justifyContent="space-between" alignItems="center" gap="small">
                <DetailsWrap>
                    <Brand>{brand}</Brand>
                    <Name>{name}</Name>
                    <Description>{description}</Description>

                    {shouldShowPersonalizationDetails && (
                        <Personalization>
                            {personalizationMetadataEntries.map((entry) => (
                                <p key={entry.key}>
                                    {entry.key}: <em>{entry.value}</em>
                                </p>
                            ))}
                        </Personalization>
                    )}
                </DetailsWrap>

                {!!quantitySelector && (
                    <Flex column>
                        {specialPriceList && !!specialPriceList?.length && (
                            <ProductPrice
                                minimumPrice={c_minimumPrice}
                                isGiftCard={isGiftCard}
                                promoType={c_activepromo?.type}
                                defaultLabel={translate('pdp.defaultPriceLabel')}
                                prices={specialPriceList}
                                moduleType="smallProduct"
                            />
                        )}

                        {quantitySelector}
                    </Flex>
                )}
                {!quantitySelector && <PriceWrap>{priceLabel}</PriceWrap>}
            </Content>
        </Flex>
    );
};
